<template>
    <div v-if="columns.length == 0" class="text-caption q-ml-md q-my-sm consistentText" style="flex: auto;">
        {{ Translate('No items visible - check your filter settings') }}
    </div>
    <q-list v-else>

        <q-expansion-item
            v-for="(col,i) in added_columns"
            :key="i"
            :ref="`panel-${i}`"
            dense
            :model-value="panels_open.includes(i)"
            @update:model-value="val => onPanelExpansionUpdate(i, val)"
            :header-class="[
                'q-px-lg',
                panels_open.includes(i) ? 'filter-expansion-header--selected' : '',
                i === 0 ? '' : 'top-border'
            ]"
            :header-style="{
                borderTop: i === 0 ? 'none' : '1px solid #d6d6d6',
                paddingTop: '12px',
                paddingBottom: '12px',
            }"
        >
            <template #header>
                <q-item-section>
                    <span>
                        {{ col.Category === '0#Metrics' ? col.Title : Translate(col.Title) }}
                    </span>
                </q-item-section>
                <q-item-section side>
                    <q-icon
                        v-if="isIconVisible(col)"
                        color="primary"
                        name="mdi-close-circle-outline"
                        :title="Translate('Clear Filter')"
                        class="q-mr-sm"
                        @click.stop="clear(col)"
                    />
                </q-item-section>
            </template>

            <div>
                <q-card flat class="q-pa-xs" style="background-color: white">
                    <div class="column q-gutter-y-xs">
                        <q-toggle
                            v-if="!ReadOnly"
                            dense
                            color="primary"
                            class="q-ma-none"
                            :model-value="usages[col.ColId] > 0"
                            @update:model-value="v => setColumnFilterUse(col, v ? 1 : 0)"
                        >
                            <template #default>
                                <span class="row items-center no-wrap">
                                    {{ Translate('Allow the filter to be used in view mode') }}
                                    <q-icon name="mdi-information" size="xs" class="q-ml-xs cursor-pointer form-hint-icon">
                                        <q-tooltip anchor="bottom middle" self="top middle" max-width="320px" transition-show="scale" transition-hide="scale">
                                            <span style="font-size: larger;">
                                                {{ Translate(`In this mode, filter controls are visible and accessible in view mode. Users can modify 
                                                the filter settings freely while viewing the report, allowing for dynamic and personalized 
                                                adjustments. The filter selections made in Edit mode will serve as the default settings 
                                                when first entering view mode, but users can freely adjust these as needed.`) }}
                                            </span>
                                        </q-tooltip>
                                    </q-icon>
                                </span>
                            </template>
                        </q-toggle>
                        <!-- :disabled included !hasFilter(col) -->
                        <q-toggle
                            v-if="!ReadOnly && limitedFilteringTypes.includes(col.SourceType)"
                            dense
                            color="primary"
                            class="q-ma-none"
                            :disable="!(usages[col.ColId] > 0)"
                            :model-value="usages[col.ColId] == 2"
                            @update:model-value="v => setColumnFilterUse(col, v ? 2 : 1)"
                        >
                            <template #default>
                                <span class="row items-center no-wrap">
                                    {{ Translate('Only allow filtering on selected items') }}
                                    <q-icon name="mdi-information" size="xs" class="q-ml-xs cursor-pointer form-hint-icon">
                                        <q-tooltip anchor="bottom middle" self="top middle" max-width="320px" transition-show="scale" transition-hide="scale">
                                            <span style="font-size: larger;">
                                                {{ Translate(`When enabled, filter controls are available in view mode with restrictions. Users can adjust 
                                                settings but only within the options defined in Edit mode. By default, filters include only selected items 
                                                (if none are selected, all are assumed selected). However, if 'All' is selected while some items remain unselected, 
                                                the filter switches to exclusive mode - excluding unselected items while including selected and newly added ones.`) }}
                                            </span>
                                        </q-tooltip>
                                    </q-icon>
                                </span>
                            </template>
                        </q-toggle>
                    </div>

                    <component v-bind:is="getColumnComponent(col)"
                               :key="column_ids[col.ColId]"
                               :item="col"
                               :EventBus="EventBus"
                               :settings="settings"
                               :isopen="isFieldOpen(i)"
                               :value="column_filters[col.ColId]"
                               :initial="initial_column_filters != null ? initial_column_filters[col.ColId] : null"
                               :advancedSettings="advancedSettings"
                               @change="(value) => localUpdateFilterValue(col, value)"></component>
                </q-card>
            </div>
        </q-expansion-item>

    </q-list>
</template>

<script>
    import common from './common.js';
    import utils from '@/Shared/utils.jsx';
    import methods from '@/Shared/methods';

    import FilterbooleanColumn from './filter_booleanColumn.vue';
    import FilterchannelTypeColumn from './filter_textSetColumn.vue';
    import FiltercountColumn from './filter_durationColumn.vue';
    import FilterfloatColumn from './filter_durationColumn.vue';
    import FiltercurrencyColumn from './filter_durationColumn.vue';
    import FiltercustomerIDColumn from './filter_textSetColumn.vue';
    import FilterdateColumn from './filter_dateColumn.vue';
    import FilterdirectionColumn from './filter_directionColumn.vue';
    import FilterdurationColumn from './filter_durationColumn.vue';
    import FilterpercentColumn from './filter_durationColumn.vue';
    import FilterphoneNumColumn from './filter_textSetColumn.vue';
    import FiltersearchableTextColumn from './filter_searchableTextColumn.vue';
    import FiltertextSetColumn from './filter_textSetColumn.vue';
    import FilteruserIDColumn from './filter_textSetColumn.vue';

    export default {
        components: {
            FilterbooleanColumn,
            FilterchannelTypeColumn,
            FiltercountColumn,
            FiltercurrencyColumn,
            FiltercustomerIDColumn,
            FilterfloatColumn,
            FilterdateColumn,
            FilterdirectionColumn,
            FilterdurationColumn,
            FilterpercentColumn,
            FilterphoneNumColumn,
            FiltersearchableTextColumn,
            FiltertextSetColumn,
            FilteruserIDColumn,
        },
        data: function () {
            return {
                id: 1,
                limitedFilterKey: 1,
                panels_open: [],
                usages: {},
                limitedFilteringTypes: [
                    'textSetColumn',
                    'channelTypeColumn',
                    'userIDColumn',
                    'customerIDColumn',
                    'phoneNumColumn',
                ],
                column_ids: {},
            }
        },
        props: {
            EventBus: null,
            columns: null,
            column_filters: Object,
            initial_column_filters: null,
            settings: null,
            ReadOnly: null,
            advancedSettings: null,

            getColumnComponent: null,
            getFilterValue: null,
            updateFilterValue: null,
        },
        created() {
            this.EventBus.$on(`ClearAllReportFilters`, this.clearAll);
            this.EventBus.$on(`SetPanelOpenState`, this.setPanelOpenState);

            // Create unique ids for each dynamic filter column so we can force the component to refresh when needed
            for (let c of this.columns)
                this.column_ids[c.ColId] = utils.generateUUID();

            for (let colid of Object.keys(this.column_filters))
                this.usages[colid] = this.column_filters[colid].usage || 0;
        },
        unmounted() {
            this.EventBus.$off(`ClearAllReportFilters`, this.clearAll);
            this.EventBus.$off(`SetPanelOpenState`, this.setPanelOpenState);
        },
        computed: {
            added_columns: function () {
                return this.columns.filter(c => !!this.column_filters[c.ColId]);
            }
        },
        methods: {
            ...methods,
            onPanelExpansionUpdate(i, expanded) {
                const arr = this.panels_open
                const idx = arr.indexOf(i)
                if (expanded) {
                    if (idx === -1) arr.push(i)
                } else if (idx !== -1) {
                    arr.splice(idx, 1)
                }
            },
            clear(col) {
                if (!this.ReadOnly) {
                    if (col.ColId in this.usages)
                        this.usages[col.ColId] = 0;
                    this.updateFilterValue(col, null);
                }
                else {
                    console.log("Inside clear for filter for " + JSON.stringify(col));
                    if (col.ColId != "datetime")
                    {
                        const initial = this.initial_column_filters[col.ColId];
                        if (initial)
                        {
                            if (initial.usage == 2)
                            {
                                    this.localUpdateFilterValue(col, { "filterType":"set", "usage": initial.usage, "cleared": 1, "values": initial.values});                              
                            }
                            else 
                            {
                                this.localUpdateFilterValue(col, {"usage": initial.usage, "cleared": 1});
                            }
                        }
                        else
                        {
                            this.updateFilterValue(col, { usage: this.usages[col.ColId] || 0 });
                        }
                    }
                }
            },
            clearAll() {
                for (let col of Object.keys(this.column_filters)) {
                    this.clear({ ColId: col });
                }
            },
            setPanelOpenState(colId) {
                for (let i = 0; i < this.added_columns.length; i++) {
                    const col = this.added_columns[i];
                    if (col.ColId == colId && !this.panels_open.includes(i)) {
                        this.panels_open.push(i);

                        // Scroll the panel into view if necessary
                        const panel = this.$refs[`panel-${i}`];
                        const inst = Array.isArray(panel) ? panel[0] : panel;
                        if (inst?.$el) inst.$el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                        break;
                    }
                }
            },
            isFieldOpen(index) {
                return this.panels_open.includes(index);
            },
            localUpdateFilterValue(col, value) {
                console.log("inside localUpdateFilterValue");
                console.log(JSON.stringify(value));

                const usage = this.usages[col.ColId] || 0;
                const existingFilter = this.column_filters[col.ColId];
                const isEmpty = !value || (typeof value === 'object' && !Array.isArray(value) && Object.keys(value).length === 0);

                // Empty criteria (e.g. All checked then unchecked) clears the applied filter
                // but must not remove it from the list. Only the X / Clear All actions do that.
                if (isEmpty || value?.cleared) {
                    if (isEmpty) {
                        // A newly added filter starts as { usage: 0 }. Some filter components emit
                        // an empty change during mount; ignore that so we don't churn the model.
                        if (existingFilter && usage === 0 && Object.keys(existingFilter).length === 1 && 'usage' in existingFilter)
                            return;

                        if (usage > 0) {
                            const initial = this.initial_column_filters?.[col.ColId];
                            if (usage == 2 && initial?.values) {
                                this.updateFilterValue(col, {
                                    filterType: 'set',
                                    usage,
                                    cleared: 1,
                                    values: initial.values,
                                    mode: initial.mode,
                                });
                            } else {
                                this.updateFilterValue(col, { usage, cleared: 1 });
                            }
                        } else {
                            this.updateFilterValue(col, { usage: 0 });
                        }
                    } else {
                        this.updateFilterValue(col, { ...value, usage: value.usage ?? usage });
                    }

                    if (!this.hasFilter(col))
                        this.limitedFilterKey++;
                    return;
                }

                const hasFilterContent = value.filterType || value.type || value.filter !== undefined || value.filterTo !== undefined || value.values;
                if (!hasFilterContent) {
                    if (!usage)
                        this.updateFilterValue(col, { usage: 0 });
                    return;
                }

                this.updateFilterValue(col, { ...value, usage });

                if (!this.hasFilter(col))
                    this.limitedFilterKey++;
            },

            getColumnFilterUse(col) {
                return this.usages[col.ColId] || 0;
            },
            setColumnFilterUse(col, value) {
                console.log("Inside setColumnFilterUse");
                this.usages[col.ColId] = value;
                let filter;

                if (value || this.column_filters[col.ColId])
                    filter = { ...this.column_filters[col.ColId], usage: value || 0 };
                else
                    filter = {};

                // Ensure we don't end up with a blank filter with usage = 0
                if (Object.keys(filter).length == 1 && 'usage' in filter && !filter.usage)
                    filter = {};

                this.updateFilterValue(col, filter);
            },
            hasFilter(col) {
                const filter = this.column_filters[col.ColId];
                if (!filter) return false;

                if ((Object.keys(filter).length == 1 && 'usage' in filter) || filter.filter == '{{CurrentUserID}}')
                    return false;

                return true;
            },

            isIconVisible(col) {
                if (this.ReadOnly && this.initial_column_filters)
                {
                    const c = this.column_filters[col.ColId];
                    if (!c)
                    {
                        return false;
                    } 
                    if ('usage' in c && Object.keys(c).length > 1)
                    {
                        if (c.usage == 2) {
                            // Compare initial to current filter
                            const i = this.initial_column_filters[col.ColId];
                            // If not same, icon is visible
                            return !common.compareObjects(c, i) && !c.cleared;
                        }
                        else
                        {
                            return !c.cleared;
                        }
                    }
                    else
                    {
                          return false; // don't show that a filter is present if it is baked
                    }
                }
                else
                {
                  return col.ColId in this.column_filters;   
                }
            },
        }
    }
</script>

<style scoped>
    .consistentText {
        color: var(--v-black-base) !important; 
        opacity: 0.8 !important; 
        font-size: 14px !important; 
        text-rendering: optimizelegibility !important; 
        letter-spacing:0.1 !important;
    }

    :deep(.q-expansion-item > .q-expansion-item__container > .q-item:hover) {
        background-color: var(--v-gridHeader-base);
    }

    :deep(.q-expansion-item > .q-expansion-item__container > .q-item > .q-focus-helper) {
        display: none;
    }

    :deep(.q-expansion-item__toggle-icon) {
        color: var(--v-secondaryText-base);
    }
</style>
